import React from 'react';
import { useTranslation } from 'react-i18next';
import { formClasses } from '../../common/tailwindClassnames';

export type MapEntry = [string, string];

type ValueMappingRowsProps = {
  entries: MapEntry[];
  onChange: (entries: MapEntry[]) => void;
};

const ValueMappingRows = ({ entries, onChange }: ValueMappingRowsProps) => {
  const { t } = useTranslation();

  const updateAt = (idx: number, nextEntry: MapEntry) => {
    const next = [...entries];
    next[idx] = nextEntry;
    onChange(next);
  };

  const removeAt = (idx: number) =>
    onChange(entries.filter((_, index) => index !== idx));

  const addRow = () => onChange([...entries, ['', '']]);

  return (
    <div>
      <label className={formClasses.label}>{t('Value mappings')}</label>
      <div className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-x-2 gap-y-1">
        <span className="text-xs text-ui-text-primary-text">
          {t('When source equals')}
        </span>
        <span></span>
        <span className="text-xs text-ui-text-primary-text">
          {t('Use this value')}
        </span>
        <span></span>
        {entries.map(([key, value], idx) => {
          const rowNumber = idx + 1;
          return (
            <React.Fragment key={idx}>
              <input
                className={formClasses.input}
                type="text"
                value={key}
                aria-label={`${t('When source equals')} (${t('Value mappings')} ${rowNumber})`}
                data-testid={`FieldMappingForm-key-${idx}`}
                onChange={(event) => updateAt(idx, [event.target.value, value])}
              />
              <span className="text-ui-text-primary-text">→</span>
              <input
                className={formClasses.input}
                type="text"
                value={value}
                aria-label={`${t('Use this value')} (${t('Value mappings')} ${rowNumber})`}
                data-testid={`FieldMappingForm-value-${idx}`}
                onChange={(event) => updateAt(idx, [key, event.target.value])}
              />
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="px-1 text-ui-text-primary-text transition-colors hover:text-unlisted-orange"
                aria-label={`${t('Remove')} (${t('Value mappings')} ${rowNumber})`}
                data-testid={`FieldMappingForm-remove-row-${idx}`}
              >
                ✕
              </button>
            </React.Fragment>
          );
        })}
      </div>
      <button
        type="button"
        data-testid="FieldMappingForm-add-row"
        onClick={addRow}
        className="mt-2 text-sm font-semibold text-ui-cta-primary transition-colors hover:text-ui-cta-primary"
      >
        {t('Add row')}
      </button>
    </div>
  );
};

export default ValueMappingRows;
